import { useMutation } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { parseSandboxData } from '@/services/api/parser';
import { getApiMessage } from '@/lib/apiMessage';

interface TestParseParams {
  file: File;
  supplierName: string;
  templateId: string;
  parserType: 'ai' | 'rule' | 'hybrid';
  ocrEngine: 'tesseract' | 'google-vision';
  confidenceThreshold: number;
  customRules: any;
}

export const useParserSandbox = (supplierId?: string) => {
  // Test parse - use multipart/form-data directly
  const testParse = useMutation({
    mutationFn: async (params: TestParseParams) => {
      const data = await parseSandboxData(params.file, String(params.supplierName));
      const success = typeof (data as any)?.success === 'boolean'
        ? (data as any).success
        : (typeof (data as any)?.Success === 'boolean' ? (data as any).Success : undefined);
      const backendMessage = getApiMessage(data) || (data as any)?.message || (data as any)?.Message;
      // Treat explicit success:false as an error so UI flow doesn't proceed
      if (success === false) {
        throw new Error(backendMessage || 'Parser test failed');
      }
      // Surface backend message via mutation result for UI components
      (data as any).__message = backendMessage;
      return data;
    },
    // Important: avoid automatic retries so failures don't trigger multiple API calls
    retry: 0,
    onError: (error: any) => {
      const msg = getApiMessage(error) || error?.message || 'Parser test failed';
      toast({ title: 'Parser Error', description: msg, variant: 'destructive' });
    }
  });

  // Get parser templates by supplier (mock data for now)
  const templates = supplierId ? [
    { id: '1', name: 'Standard Template', version: '1.0', updated_at: new Date().toISOString() },
    { id: '2', name: 'Advanced Template', version: '2.0', updated_at: new Date().toISOString() },
  ] : [];

  return { testParse, templates };
};
